import { createContext, useCallback, useContext, useMemo } from "react";

import { useAuth } from "@/lib/authContext";
import { fetchCurrentAccount } from "@/api/account";

type SessionUser = {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  permissions?: string[];
  user_type?: string;
  is_owner?: boolean;
  avatar_url?: string | null;
};

type SessionOrganization = {
  id?: string;
  name?: string | null;
};

type CapabilityMap = Record<string, boolean>;

type SessionPayload = {
  user?: SessionUser | null;
  organization?: SessionOrganization | null;
  teams?: Array<Record<string, unknown>>;
  permissions?: string[];
  tier?: string | null;
  organization_tier?: string | null;
  capabilities?: CapabilityMap | null;
  credits?: number | null;
  credit_expires_at?: string | null;
  limits?: Record<string, unknown> | null;
  trial_ends_at?: string | null;
  is_on_trial?: boolean | null;
  trial_expired?: boolean | null;
  [key: string]: unknown;
} | null;

export type SessionUserContextValue = {
  session: SessionPayload;
  user: SessionUser | null;
  organization: SessionOrganization | null;
  teams: Array<Record<string, unknown>>;
  role: string | null;
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  capabilities: CapabilityMap;
  tier: string | null;
  organizationTier: string | null;
  credits: number;
  creditExpiresAt: string | null;
  limits: Record<string, unknown>;
  hasCapability: (flag: string) => boolean;
  hasAnyCapability: (flags: string[]) => boolean;
  sessionUser: SessionPayload | null;
  refreshSession?: () => Promise<void>;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  isOnTrial: boolean;
  trialExpired: boolean;
};

const SessionUserContext = createContext<SessionUserContextValue | null>(null);

const DEFAULT_CONTEXT: SessionUserContextValue = {
  session: null,
  user: null,
  organization: null,
  teams: [],
  role: null,
  permissions: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  capabilities: {},
  tier: null,
  organizationTier: null,
  credits: 0,
  creditExpiresAt: null,
  limits: {},
  hasCapability: () => false,
  hasAnyCapability: () => false,
  sessionUser: null,
  refreshSession: undefined,
  trialEndsAt: null,
  trialDaysLeft: null,
  isOnTrial: false,
  trialExpired: false,
};

function buildPermissionSet(payload: SessionPayload): Set<string> {
  const set = new Set<string>();
  if (!payload) return set;
  const push = (values?: string[] | null) => {
    if (!values) return;
    values.forEach((value) => {
      if (value) {
        set.add(value);
      }
    });
  };
  push(payload.permissions as string[] | undefined);
  const userPermissions = Array.isArray(payload.user?.permissions) ? payload.user?.permissions : [];
  push(userPermissions as string[]);
  return set;
}

function buildCapabilityMap(payload: SessionPayload): CapabilityMap {
  const map: CapabilityMap = {};
  if (!payload) return map;
  const merge = (source?: CapabilityMap | null) => {
    if (!source || typeof source !== "object") return;
    Object.entries(source).forEach(([key, value]) => {
      if (!key) return;
      map[key] = Boolean(value);
    });
  };
  merge(payload.capabilities);
  if (payload.user && typeof payload.user === "object") {
    const userCaps = (payload.user as { capabilities?: CapabilityMap | null }).capabilities;
    merge(userCaps);
  }
  const nestedSession = (payload as { session?: { capabilities?: CapabilityMap } }).session;
  if (nestedSession) {
    merge(nestedSession.capabilities);
  }
  return map;
}

export function SessionUserProvider({ children }: { children: React.ReactNode }) {
  const { profile, accessToken, completeLogin } = useAuth();
  const session = useMemo<SessionPayload>(() => {
    if (!profile || typeof profile !== "object") return null;
    return profile as SessionPayload;
  }, [profile]);
  const sessionUser = session ?? null;
  const refreshSession = useCallback(async () => {
    if (!accessToken) return;
    try {
      const updatedProfile = await fetchCurrentAccount();
      completeLogin(accessToken, updatedProfile as Record<string, unknown>);
    } catch {
      // no-op: keep existing session if refresh fails
    }
  }, [accessToken, completeLogin]);
  const permissionSet = useMemo(() => buildPermissionSet(session), [session]);
  const capabilityMap = useMemo(() => buildCapabilityMap(session), [session]);
  const value = useMemo<SessionUserContextValue>(() => {
    const user = session?.user ?? null;
    const permissions = Array.from(permissionSet);
    const tierValue = typeof session?.tier === "string" ? session.tier : user?.role ?? null;
    const orgTierValue =
      typeof session?.organization_tier === "string"
        ? session.organization_tier
        : ((session?.organization as { tier?: string })?.tier as string | undefined) ?? null;
    const creditsValue = typeof session?.credits === "number" ? session.credits : 0;
    const limitsValue =
      (session?.limits && typeof session.limits === "object" ? (session.limits as Record<string, unknown>) : {}) ?? {};
    const hasPermission = (permission: string) => {
      if (!permission) return true;
      return permissionSet.has(permission);
    };
    const hasAnyPermission = (list: string[]) => {
      if (!list || list.length === 0) return true;
      return list.some((permission) => permissionSet.has(permission));
    };
    const hasCapability = (flag: string) => {
      if (!flag) return true;
      return Boolean(capabilityMap[flag]);
    };
    const hasAnyCapability = (flags: string[]) => {
      if (!flags || flags.length === 0) return true;
      return flags.some((flag) => capabilityMap[flag]);
    };
    const trialEndsAt = (session?.trial_ends_at as string | undefined) ?? null;
    const isOnTrial = Boolean(session?.is_on_trial);
    const trialExpired = Boolean(session?.trial_expired);
    const trialDaysLeft =
      trialEndsAt && isOnTrial
        ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000))
        : null;

    return {
      session,
      user,
      organization: session?.organization ?? null,
      teams: Array.isArray(session?.teams) ? (session?.teams as Array<Record<string, unknown>>) : [],
      role: user?.role ?? (session?.role as string | undefined) ?? null,
      permissions,
      hasPermission,
      hasAnyPermission,
      capabilities: capabilityMap,
      tier: tierValue ?? null,
      organizationTier: orgTierValue ?? null,
      credits: creditsValue || 0,
      creditExpiresAt: (session?.credit_expires_at as string | undefined) ?? null,
      limits: limitsValue,
      hasCapability,
      hasAnyCapability,
      sessionUser,
      refreshSession,
      trialEndsAt,
      trialDaysLeft,
      isOnTrial,
      trialExpired,
    };
  }, [session, permissionSet, capabilityMap]);

  return <SessionUserContext.Provider value={value}>{children}</SessionUserContext.Provider>;
}

export function useSessionUser(): SessionUserContextValue {
  const ctx = useContext(SessionUserContext);
  if (!ctx) return DEFAULT_CONTEXT;
  return ctx;
}
